import createBrowserHistory from 'history/createBrowserHistory';
import * as React from 'react';
import { Provider } from 'react-redux';
import { YMInitializer } from 'react-yandex-metrika';
import { Store } from 'redux';
import { initializeCurrentLocation } from 'redux-little-router';

import {
    fetchCommonData,
    rejectRequestAuthorizationData,
    requestAuthorizationData
} from '../../actions/common';
import { fetchClientDevInfoAction } from '../../actions/development-tools';
import { updateDevInfo } from '../../actions/devInfo';
import { BannersDataProvider } from '../../api/banners/BannersDataProvider';
import { BannersMutationExecutor } from '../../api/banners/BannersMutationExecutor';
import { SitelinksMutationExecutor } from '../../api/banners/SitelinksMutationExecutor';
import { CalloutsMutation } from '../../api/callouts/CalloutsMutation';
import { CampaignsDataProvider } from '../../api/campaigns/CampaignsDataProvider';
import { CampaignsMutationExecutor } from '../../api/campaigns/CampaignsMutationExecutor';
import { ClientDevProvider } from '../../api/ClientDevProvider/ClientDevProvider';
import { CommonDataProvider } from '../../api/CommonDataProvider';
import { DealsDataProvider } from '../../api/deals/DealsDataProvider';
import { DialogsDataProvider } from '../../api/dialogs/DialogsDataProvider';
import { DialogsMutationExecutor } from '../../api/dialogs/DialogsMutationExecutor';
import { DocCenterRestApiMethodExecutor } from '../../api/DocCenterRestApiMethodExecutor/DocCenterRestApiMethodExecutor';
import { GoalsDataProvider } from '../../api/goals/GoalsDataProvider';
import { EntitesDataProvider } from '../../api/grid/EntitiesDataProvider';
import { FreelancerDataProvider } from '../../api/grid/FreelancerDataProvider';
import { FreelancerMutationExecutor } from '../../api/grid/FreelancerMutationExecutor';
import { PhrasesMutationExecutor } from '../../api/grid/PhrasesMutationExecutor/PhrasesMutationExecutor';
import { PhrasesWizardDataProvider } from '../../api/grid/PhrasesWizardDataProvider';
import { PricesWizardMutationExecutor } from '../../api/grid/PricesWizardMutationExecutor';
import { GroupsDataProvider } from '../../api/groups/GroupsDataProvider';
import { GroupsMutationExecutor } from '../../api/groups/GroupsMutationExecutor';
import { IncognitoDataProvider } from '../../api/IncognitoDataProvider';
import { JavaRestApiMethodExecutor } from '../../api/JavaRestApiMethodExecutor/JavaRestApiMethodExecutor';
import { MarketersDataProvider } from '../../api/MarketersDataProvider';
import { MinusPhraseLibraryProvider } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { MinusPhraseMutationExecutor } from '../../api/MinusPhraseLibraryProvider/MinusPhraseMutationExecutor';
import { OrganizationDataProvider } from '../../api/organization/OrganizationDataProvider';
import { OrganizationMutationExecutor } from '../../api/organization/OrganizationMutationExecutor';
import { PerlRestApiMethodExecutor } from '../../api/PerlRestApiMethodExecutor/PerlRestApiMethodExecutor';
import { PredictorProvider } from '../../api/predictor/PredictorProvider';
import { ProductsDataProvider } from '../../api/ProductsDataProvider';
import { PythonRestApiMethodExecutor } from '../../api/PythonRestApiMethodExecutor/PythonRestApiMethodExecutor';
import { RecommendationsDataProvider } from '../../api/recommendations/RecommendationsDataProvider';
import { RecommendationsMutationExecutor } from '../../api/recommendations/RecommendationsMutationExecutor';
import { ReportsDomainDataProvider } from '../../api/reports/ReportsDomainDataProvider';
import { RestApiManager } from '../../api/RestApiManager';
import { RetargetingDataProvider } from '../../api/retargeting/RetargetingDataProvider';
import { RetargetingMutationExecutor } from '../../api/retargeting/RetargetingMutationExecutor';
import { SmartFiltersDataProvider } from '../../api/smartFilters/SmartFiltersDataProvider';
import { SmartFiltersMutationExecutor } from '../../api/smartFilters/SmartFiltersMutationExecutor';
import { TurboDataProvider } from '../../api/TurboDataProvider/TurboDataProvider';
import { ApiManagerProvider } from '../../containers/ApiManagerProvider/ApiManagerProvider';
import {
    getDNAApolloClient,
    getIncognitoApolloClient
} from '../../declarations/apollo';
import { mapOfficeContactsApiData } from '../../mappers/common/common';
import { InitializationData } from '../../reducers';
import { defaultDevInfo } from '../../reducers/devInfo';
import { getCSRFToken } from '../../service/ApiHelpers/ApiHelpers';
import { getFetchWrapper } from '../../service/Captcha/Captcha';
import { captureException } from '../../service/ErrorsReporting/ErrorsReporting';
import { getGridInitialData } from '../../service/GridHelpers/GridHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { RUM } from '../../service/RUM/RUM';
import { SegmentManager } from '../../service/SegmentManager';
import {
    EpicDeps,
    getStore,
    getStoreForDevEnv
} from '../../service/StoreFactory';
import { UrlHelpers } from '../../service/UrlHelpers';
import { YandexMapHelper } from '../../service/YandexMapHelper/YandexMapHelper';
import { GRID_PREFIX, OfficeApiContacts } from '../../typings/common';
import { DevInfo } from '../../typings/dev';
import { CampaignType } from '../../typings/domain';
import { LogParams } from '../../typings/log';
import { ReportParams } from '../../typings/reports';
import { State } from '../../typings/state';
import { WelcomeScreenParams } from '../../typings/welcome';
import { App } from '../App/App';

const isProduction = process.env.NODE_ENV === 'production';
const perlApiPath = '/registered';
const javaApiPath = '/web-api';
const pythonApiPath = '/public';
const fetchWrapper = getFetchWrapper();
const perlApiMethodExecutor = new PerlRestApiMethodExecutor(
    perlApiPath,
    fetchWrapper
);
const javaApiMethodExecutor = new JavaRestApiMethodExecutor(
    javaApiPath,
    fetchWrapper
);

const pythonApiMethodExecutor = new PythonRestApiMethodExecutor(
    pythonApiPath,
    fetchWrapper
);

const apiManager = new RestApiManager(
    perlApiMethodExecutor,
    javaApiMethodExecutor,
    pythonApiMethodExecutor
);

// init momentjs locale
LangUtils.init();
// init start timer
RUM.registerApiManager(apiManager);

const docCenterApiMethodExecutor = new DocCenterRestApiMethodExecutor(
    UrlHelpers.getDocCenterUrl(),
    LangUtils.getCurrentLang()
);

if (process.env.DEBUG_UPDATE === '1') {
    const whyDidYouRender = require('@welldone-software/why-did-you-render');

    whyDidYouRender(React, {
        include: [/.*/],
        exclude: [/Context/]
    });
}

const url = new URLSearchParams(window.location.search);
let fromOldInterface = false;
// В случае редиректа с паспорта, перл подставляет скрытый URL.
// На чистых урлах он не матчится, поэтому необходимо перевести его на правильные.
// Пример урла /registered/main.pl?authredirlevel=1536842559.0&cmd=showDna&context=grid&ulogin=alisecar17
if (url.get('cmd') === 'showDna') {
    const context = url.get('native_uri') || `dna/${GRID_PREFIX}`;
    const login = url.get('ulogin');
    const search = login ? `?ulogin=${login}` : '';

    window.location.href = `${context}${search}`;
}

if (url.get('from_old_interface') === '1') {
    fromOldInterface = true;
}

interface RootProps {
    login: string;
    logParams?: LogParams;
    reportClientParams?: ReportParams;
    reportDomainParams?: ReportParams;
    welcomePageParams?: WelcomeScreenParams;
    enableRecommendations: boolean;
    gridAllowed: boolean;
    rmpEnabled: boolean;
    role?: string;
    dealsEnabled: boolean;
    domainsReportEnabled: boolean;
    dealsReadonly: boolean;
    minusPhraseLibraryEnabled: boolean;
    isCampaignUpdateAllowed: boolean;
    isHeaderAvailable: boolean;
    enableSidebar: boolean;
    isWebvisorEnabled: boolean;
}

interface StoreParams {
    login: string;
    isPublicDomain?: boolean;
    officeContacts: OfficeApiContacts;
    availableCampaignsTypes: CampaignType[];
    role?: string;
    devInfo?: DevInfo;
    perlReqId?: string;
    welcomeParams?: {
        country: string;
        currency: string;
    };
}

export class Root extends React.Component<RootProps> {
    private static store: Store<State>;

    public static initStore(opts: StoreParams) {
        const epicDeps = Root.getEpicDeps(opts.login);
        const history = createBrowserHistory();
        const initData: InitializationData = {
            commonData: {
                login: opts.login,
                publicDomain: opts.isPublicDomain || false,
                sidebar: {
                    expanded: false
                },
                operator: {
                    officeInfo: mapOfficeContactsApiData(opts.officeContacts),
                    isAgency: false,
                    isSelfAgency: false,
                    login: ''
                },
                fromOldInterface,
                tld: UrlHelpers.getTld()
            },
            header: {
                availableCampaignsTypes: opts.availableCampaignsTypes,
                role: opts.role
            },
            grid: getGridInitialData(opts.login),
            welcome: opts.welcomeParams
                ? {
                      baseSettings: opts.welcomeParams
                  }
                : undefined
        };

        apiManager.setApolloClient(
            getDNAApolloClient(opts.login, getCSRFToken)
        );

        const store = isProduction
            ? getStore(apiManager, history, epicDeps, initData)
            : getStoreForDevEnv(apiManager, history, epicDeps, initData);

        YandexMapHelper.setApiManager(epicDeps.bannersDataProvider);

        const initialState = store.getState();

        if (initialState && initialState.router) {
            store.dispatch(initializeCurrentLocation(initialState.router));
        }

        if (opts.login) {
            store.dispatch(requestAuthorizationData());
        } else {
            store.dispatch(rejectRequestAuthorizationData());
        }
        store.dispatch(fetchCommonData(opts.login));

        const realDevInfo = opts.devInfo || defaultDevInfo;
        if (realDevInfo.isBeta) {
            store.dispatch(updateDevInfo(realDevInfo));
            store.dispatch(fetchClientDevInfoAction());
        }

        Root.store = store;

        RUM.reportIfFailedToInit(opts.login, opts.perlReqId);
    }

    private static getEpicDeps(login: string): EpicDeps {
        const dnaClient = getDNAApolloClient(login, getCSRFToken);
        const entitiesDataProvider = new EntitesDataProvider(dnaClient);
        const phraseQueryProvider = new PhrasesWizardDataProvider(dnaClient);
        const recommendationsDataProvider = new RecommendationsDataProvider(
            dnaClient
        );
        const commonDataProvider = new CommonDataProvider(dnaClient);
        const segmentManager = new SegmentManager();
        const pricesWizardMutationExecutor = new PricesWizardMutationExecutor(
            dnaClient
        );
        const minusPhraseLibraryDataProvider = new MinusPhraseLibraryProvider(
            dnaClient
        );
        const minusPhraseLibraryMutationExecutor = new MinusPhraseMutationExecutor(
            dnaClient
        );
        const freelancerMutationExecutor = new FreelancerMutationExecutor(
            dnaClient
        );
        const phrasesMutationExecutor = new PhrasesMutationExecutor(dnaClient);

        const recommendationsMutationExecutor = new RecommendationsMutationExecutor(
            dnaClient
        );

        const dealsDataProvider = new DealsDataProvider(apiManager, dnaClient);

        const freelancerDataProvider = new FreelancerDataProvider(dnaClient);

        const incognitoClient = getIncognitoApolloClient(getCSRFToken);

        const incognitoDataProvider = new IncognitoDataProvider(
            incognitoClient
        );

        const bannersDataProvider = new BannersDataProvider(dnaClient);
        const bannersMutationExecutor = new BannersMutationExecutor(dnaClient);
        const sitelinksMutationExecutor = new SitelinksMutationExecutor(
            dnaClient
        );
        const reportsDomainDataProvider = new ReportsDomainDataProvider(
            dnaClient
        );
        const calloutsMutation = new CalloutsMutation(dnaClient);

        const campaignsDataProvider = new CampaignsDataProvider(dnaClient);
        const campaignsMutationExecutor = new CampaignsMutationExecutor(
            dnaClient
        );
        const groupsDataProvider = new GroupsDataProvider(dnaClient);
        const groupsMutationExecutor = new GroupsMutationExecutor(dnaClient);

        const turboDataProvider = new TurboDataProvider(dnaClient);
        const smartFiltersMutationExecutor = new SmartFiltersMutationExecutor(
            dnaClient
        );
        const smartFiltersDataProvider = new SmartFiltersDataProvider(
            dnaClient
        );

        const retargetingDataProvider = new RetargetingDataProvider(dnaClient);
        const retargetingMutationExecutor = new RetargetingMutationExecutor(
            dnaClient
        );

        const organizationDataProvider = new OrganizationDataProvider(
            dnaClient
        );

        const dialogsDataProvider = new DialogsDataProvider(dnaClient);
        const dialogsMutationExecutor = new DialogsMutationExecutor(dnaClient);

        const goalsDataProvider = new GoalsDataProvider(dnaClient);

        const clientDevProvider = new ClientDevProvider(dnaClient);

        const productsDataProvider = new ProductsDataProvider(
            apiManager,
            dnaClient
        );

        const marketersDataProvider = new MarketersDataProvider(
            apiManager,
            dnaClient
        );

        const predictorProvider = new PredictorProvider(dnaClient);

        const organizationMutationExecutor = new OrganizationMutationExecutor(
            dnaClient
        );

        return {
            client: dnaClient,
            docCenterApi: docCenterApiMethodExecutor,
            restApiManager: apiManager,
            entitiesDataProvider,
            commonDataProvider,
            recommendationsDataProvider,
            reportsDomainDataProvider,
            segmentManager,
            phrasesMutationExecutor,
            pricesWizardMutationExecutor,
            freelancerMutationExecutor,
            freelancerDataProvider,
            recommendationsMutationExecutor,
            dealsDataProvider,
            phraseQueryProvider,
            incognitoDataProvider,
            bannersDataProvider,
            bannersMutationExecutor,
            sitelinksMutationExecutor,
            groupsDataProvider,
            groupsMutationExecutor,
            smartFiltersMutationExecutor,
            smartFiltersDataProvider,
            turboDataProvider,
            calloutsMutation,
            retargetingDataProvider,
            retargetingMutationExecutor,
            organizationDataProvider,
            organizationMutationExecutor,
            minusPhraseLibraryDataProvider,
            minusPhraseLibraryMutationExecutor,
            dialogsDataProvider,
            dialogsMutationExecutor,
            goalsDataProvider,
            clientDevProvider,
            campaignsDataProvider,
            campaignsMutationExecutor,
            productsDataProvider,
            marketersDataProvider,
            predictorProvider
        };
    }

    render() {
        const {
            login,
            logParams,
            reportClientParams,
            reportDomainParams,
            domainsReportEnabled,
            welcomePageParams,
            enableRecommendations,
            gridAllowed,
            rmpEnabled,
            dealsEnabled,
            dealsReadonly,
            isHeaderAvailable,
            enableSidebar,
            minusPhraseLibraryEnabled,
            isCampaignUpdateAllowed,
            isWebvisorEnabled
        } = this.props;

        return (
            // https://github.com/apollographql/react-apollo/pull/729
            <>
                <Provider store={Root.store}>
                    <ApiManagerProvider api={apiManager}>
                        <App
                            fromOldInterface={fromOldInterface}
                            login={login}
                            isHeaderAvailable={isHeaderAvailable}
                            logParams={logParams}
                            welcomePageParams={welcomePageParams}
                            reportsClientParams={reportClientParams}
                            reportsDomainParams={reportDomainParams}
                            enableRecommendations={enableRecommendations}
                            domainsReportEnabled={domainsReportEnabled}
                            gridAllowed={gridAllowed}
                            rmpEnabled={rmpEnabled}
                            dealsEnabled={dealsEnabled}
                            dealsReadonly={dealsReadonly}
                            enableSidebar={enableSidebar}
                            minusPhraseLibraryEnabled={
                                minusPhraseLibraryEnabled
                            }
                            isCampaignUpdateAllowed={isCampaignUpdateAllowed}
                        />
                    </ApiManagerProvider>
                </Provider>
                <YMInitializer
                    version={isWebvisorEnabled ? '2' : '1'}
                    accounts={[34, 191494, 49312303]}
                    options={{
                        webvisor: isWebvisorEnabled,
                        clickmap: true,
                        trackLinks: true,
                        accurateTrackBounce: true,
                        triggerEvent: true
                    }}
                />
            </>
        );
    }

    componentDidCatch(error: Error, errorInfo: object) {
        captureException(error, { extra: errorInfo });
    }
}
